import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, resolveComponent as _resolveComponent, withCtx as _withCtx, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = ["value", "selected"]
const _hoisted_3 = { key: 1 }

import {useBriefConditions} from '@/brief-hub/brief/useBriefConditions';
    import StrategyBriefHubStepLayout from '@/brief-hub/common/ui/layout/StrategyBriefHubStepLayout.vue';
    import {DI} from '@/brief-hub/config/DependencyInjection';
    import {TjmRecommendationService} from '@/brief-hub/deposit/conditions/TjmRecommendationService';
    import {useBriefStrategyStore} from '@/brief-hub/stores/briefStrategy.store';
    import StrategyBudgetTooltip from '@/brief-hub/strategy/deposit/conditions/StrategyBudgetTooltip.vue';
    import {BriefStartDateFlexibility, BriefStartDateType, FreelancerAvailability, StartDateType} from '@/common/api/api-raw-types';
    import ProjectDurationField, {ProjectDurationModel} from '@/common/components/ProjectDurationField.vue';
    import ProjectStartDate, {ProjectStartDateModel} from '@/common/components/start-date/ProjectStartDate.vue';
    import {useBriefAmplitudeEvent} from '@/common/composable/useBriefAmplitudeEvent';
    import {getPageCountryCode, getPageLocale} from '@malt/dom';
    import {useGlobalProperties} from '@malt/vue3-use';
    import {useVuelidate} from '@vuelidate/core';
    import {Ref, computed, inject, ref, watch} from 'vue';

    interface StringSelection {
        label: string;
        value: string;
    }

    
export default /*@__PURE__*/_defineComponent({
  __name: 'StrategyBriefConditionsWhenV2',
  setup(__props) {

    const briefStrategyStore = useBriefStrategyStore();
    const {triggerBriefFunnelStepEnteredAmplitudeEvent} = useBriefAmplitudeEvent();

    const {$msg, $features} = useGlobalProperties();

    const freelancerAvailabilities = ref<StringSelection[]>([]);

    const constraints = {
        MIN_DURATION: 1,
        MAX_DURATION: 1000,
        MIN_FREELANCER_RATE: getPageCountryCode() === 'ES' ? 100 : 125,
        MAX_FREELANCER_RATE: 20000,
        MIN_BUDGET: 1,
        MAX_BUDGET: 9999999,
    };

    const tjmRecommendationService = inject<TjmRecommendationService>(DI.tjmRecommendationService);

    const selectedStartDate = ref(briefStrategyStore.briefStrategyConditionsWhen.selectedStartDate || new Date());
    const selectedAvailability = ref(briefStrategyStore.briefStrategyConditionsWhen.selectedAvailability || FreelancerAvailability.FULL_TIME);
    const selectedBudget = ref<number | undefined>(briefStrategyStore.briefStrategyConditionsWhen.selectedBudget || undefined);
    const briefCurrencyCode = ref(briefStrategyStore.briefStrategyConditionsWhen.briefCurrencyCode);
    const selectedDailyFreelancerRate = ref(briefStrategyStore.briefStrategyConditionsWhen.selectedDailyFreelancerRate);
    const briefExecFreelancerProfile = ref(briefStrategyStore.briefStrategyFreelancerProfile);
    const selectedStartDateField: Ref<ProjectStartDateModel | undefined> = ref({
        startDateType: briefStrategyStore.briefStrategyConditionsWhen.selectedStartDateType || StartDateType.ASAP,
        startDate: briefStrategyStore.briefStrategyConditionsWhen.selectedStartDate || new Date(),
        startDateFlexibility: briefStrategyStore.briefStrategyConditionsWhen.selectedStartDateFlexibility || undefined,
    });
    const selectedDurationField: Ref<ProjectDurationModel | undefined> = ref(briefStrategyStore.briefStrategyConditionsWhen.selectedDuration);

    const selectedDurationEstimation = computed<string>(() => (moreThanOneMonth.value ? 'SUPERIOR_ONE_MONTH' : 'INFERIOR_ONE_MONTH'));
    const tjmRecommendation = ref<number | 0>(0);

    const {isDurationMoreThanOneMonth} = useBriefConditions(briefStrategyStore.brief?.briefDetails!, getPageLocale());
    const moreThanOneMonth = ref<boolean>(false);

    function onSelectedAvailabilityChange(availability: FreelancerAvailability | null) {
        selectedAvailability.value = availability;
    }

    function initFreelancerAvailabilities() {
        const labelFor = (frequencyValue: FreelancerAvailability) =>
            $msg(`customer.brief.exec.form.conditions.when.project.frequency.${frequencyValue}`);

        freelancerAvailabilities.value = [
            FreelancerAvailability.ONE_DAY_PER_WEEK,
            FreelancerAvailability.TWO_DAYS_PER_WEEK,
            FreelancerAvailability.THREE_DAYS_PER_WEEK,
            FreelancerAvailability.FOUR_DAYS_PER_WEEK,
            FreelancerAvailability.FULL_TIME,
        ].map((freelancerAvailability) => ({
            label: labelFor(freelancerAvailability),
            value: freelancerAvailability,
        }));
    }

    async function retrieveTjmRecommendation(): Promise<void> {
        const category = briefExecFreelancerProfile.value.selectedCategory;
        const experienceLevel = briefExecFreelancerProfile.value.selectedExperienceLevel;
        if (category && experienceLevel) {
            const [data, error] = await tjmRecommendationService!.retrieveTjmRecommendation(category, experienceLevel);
            if (data && !error) {
                tjmRecommendation.value = data;
            }
        }
    }

    async function init() {
        initFreelancerAvailabilities();
        await retrieveTjmRecommendation();
        triggerBriefFunnelStepEnteredAmplitudeEvent('condition_when', 5);
    }

    const v$ = useVuelidate();

    init();

    const validateForm = computed(() => {
        return !!(
            !v$.value.$invalid &&
            selectedStartDateField.value &&
            (selectedStartDateField.value.startDateType === StartDateType.CHOSEN_DATE ? selectedStartDateField.value.startDate : true) &&
            selectedDurationField.value?.value &&
            selectedDurationField.value.value >= constraints.MIN_DURATION &&
            selectedDurationField.value.value <= constraints.MAX_DURATION &&
            selectedDurationField.value?.unit &&
            (moreThanOneMonth.value
                ? selectedAvailability.value
                : selectedBudget.value && selectedBudget.value >= constraints.MIN_BUDGET && selectedBudget.value <= constraints.MAX_BUDGET)
        );
    });

    function onFormChange() {
        if (validateForm.value) {
            briefStrategyStore.updatePayload({
                type: 'StrategyWhenConditionsBriefRequest',
                payload: {
                    budget: moreThanOneMonth.value ? undefined : selectedBudget.value,
                    freelancerAvailability: moreThanOneMonth.value ? selectedAvailability.value : null,
                    projectDuration: {
                        unit: selectedDurationField.value!.unit!,
                        value: parseInt(`${selectedDurationField.value?.value}`),
                        fixed: selectedDurationField.value!.fixed,
                    },
                    startDate:
                        selectedStartDateField.value?.startDateType === StartDateType.CHOSEN_DATE && selectedStartDateField.value?.startDate
                            ? selectedStartDateField.value?.startDate.toISOString()
                            : undefined,
                    startDateType: selectedStartDateField.value?.startDateType as unknown as BriefStartDateType,
                    startDateFlexibility: selectedStartDateField.value?.startDateFlexibility as unknown as BriefStartDateFlexibility,
                },
            });
        } else {
            briefStrategyStore.resetPayload();
        }
    }

    watch([selectedStartDateField, selectedAvailability, selectedDurationField, selectedBudget], onFormChange, {immediate: true});

    watch(selectedDurationField, () => {
        moreThanOneMonth.value = isDurationMoreThanOneMonth(selectedDurationField.value!.value, selectedDurationField.value!.unit);
    });

return (_ctx: any,_cache: any) => {
  const _component_VJoySelect = _resolveComponent("VJoySelect")!
  const _component_VJoyLabel = _resolveComponent("VJoyLabel")!
  const _component_VJoyInput = _resolveComponent("VJoyInput")!
  const _component_VJoyWrapper = _resolveComponent("VJoyWrapper")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(StrategyBriefHubStepLayout, { "has-sidebar": true }, {
      right: _withCtx(() => [
        _createVNode(StrategyBudgetTooltip, {
          dailyFreelancerRate: selectedDailyFreelancerRate.value || 0,
          durationEstimation: selectedDurationEstimation.value!,
          duration: selectedDurationField.value?.value || 0,
          durationUnit: selectedDurationField.value?.unit,
          startDate: selectedStartDate.value,
          availability: selectedAvailability.value,
          budget: selectedBudget.value || 0,
          constraints: constraints,
          "brief-freelancer-profile": briefExecFreelancerProfile.value
        }, null, 8, ["dailyFreelancerRate", "durationEstimation", "duration", "durationUnit", "startDate", "availability", "budget", "brief-freelancer-profile"])
      ]),
      title: _withCtx(() => [
        _createTextVNode(_toDisplayString(_unref($msg)('customer.brief.exec.form.conditions.when.title')), 1)
      ]),
      default: _withCtx(() => [
        _createVNode(_component_VJoyWrapper, {
          direction: "column",
          align: "stretch",
          "no-margin": ""
        }, {
          default: _withCtx(() => [
            _createVNode(ProjectStartDate, {
              "model-value": selectedStartDateField.value,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = (newValue) => (selectedStartDateField.value = newValue))
            }, null, 8, ["model-value"]),
            _createVNode(ProjectDurationField, {
              "model-value": selectedDurationField.value,
              label: _unref($msg)('client.project.component.project.duration.label.strategy'),
              "onUpdate:modelValue": _cache[1] || (_cache[1] = (newValue) => (selectedDurationField.value = newValue))
            }, null, 8, ["model-value", "label"]),
            (moreThanOneMonth.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                  _createVNode(_component_VJoySelect, {
                    id: "selectedAvailability",
                    modelValue: selectedAvailability.value,
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((selectedAvailability).value = $event)),
                    "data-testid": "brief-strategy-availability",
                    label: _unref($msg)('customer.brief.exec.form.conditions.when.project.frequency'),
                    required: "",
                    "aria-label": "select-availability",
                    onValueChange: onSelectedAvailabilityChange
                  }, {
                    default: _withCtx(() => [
                      _cache[4] || (_cache[4] = _createElementVNode("option", {
                        value: "",
                        disabled: ""
                      }, "-", -1)),
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(freelancerAvailabilities.value, (availability) => {
                        return (_openBlock(), _createElementBlock("option", {
                          key: availability.value,
                          value: availability.value,
                          selected: selectedAvailability.value === availability.value
                        }, _toDisplayString(availability.label) + " " + _toDisplayString(_unref($msg)('customer.brief.exec.form.conditions.when.project.frequency.by.week')), 9, _hoisted_2))
                      }), 128))
                    ]),
                    _: 1
                  }, 8, ["modelValue", "label"])
                ]))
              : _createCommentVNode("", true),
            (!moreThanOneMonth.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                  _createVNode(_component_VJoyLabel, { for: selectedBudget.value }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_unref($msg)('customer.brief.exec.form.conditions.when.project.budget')), 1)
                    ]),
                    _: 1
                  }, 8, ["for"]),
                  _createVNode(_component_VJoyInput, {
                    id: "selectedBudget",
                    modelValue: selectedBudget.value,
                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((selectedBudget).value = $event)),
                    "data-testid": "brief-strategy-budget",
                    name: "selectedBudget",
                    type: "number",
                    placeholder: 0,
                    min: constraints.MIN_BUDGET,
                    max: constraints.MAX_BUDGET,
                    unit: briefCurrencyCode.value
                  }, null, 8, ["modelValue", "min", "max", "unit"])
                ]))
              : _createCommentVNode("", true)
          ]),
          _: 1
        })
      ]),
      _: 1
    })
  ]))
}
}

})